import { Box, Stack, Typography } from "@mui/material";
import { darken, lighten, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage.ts";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { CfaTerms } from "./cfaTerms.ts";

/**
 * The base costs are drawn in two tints of the primary colour: the part due
 * whatever the outcome, and the lighter part that is lost with the case.
 */
const GUARANTEED_LIGHTEN = 0.2;
const AT_RISK_LIGHTEN = 0.6;

/**
 * The uplift of the first step is the positive colour lifted by
 * STEP_LIGHTEN_FIRST, the second the colour itself, and every further step
 * STEP_DARKEN_PER_STEP deeper. Past STEP_SHADES steps the deepest shade is
 * shared, so a long ladder does not fade to black.
 */
const STEP_LIGHTEN_FIRST = 0.12;
const STEP_DARKEN_PER_STEP = 0.16;
const STEP_SHADES = 5;

/** A bar part narrower than this share of the axis has no room for its amount. */
const MIN_LABELLED_SHARE = 10;

/** The uplift shade of a step: deeper as the win gets better. */
export const stepColor = (theme: Theme, index: number) => {
  const { main } = theme.palette.data.positive;
  const rank = Math.min(index, STEP_SHADES - 1);
  return rank === 0
    ? lighten(main, STEP_LIGHTEN_FIRST)
    : darken(main, (rank - 1) * STEP_DARKEN_PER_STEP);
};

const Swatch = ({ color }: { color: string }) => {
  const { theme } = useTheme();
  return (
    <Box
      sx={{
        width: theme.spacing(1),
        height: theme.spacing(1),
        borderRadius: "2px",
        bgcolor: color,
        flex: "none",
      }}
    />
  );
};

const RowHeading = ({
  title,
  caption,
  value,
  color,
  muted = false,
}: {
  title: string;
  caption: string;
  value: string;
  color?: string;
  muted?: boolean;
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{ alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}
  >
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", minWidth: 0 }}
    >
      {color && <Swatch color={color} />}
      <Typography
        variant="caption"
        noWrap
        sx={{
          fontWeight: 700,
          letterSpacing: ".03em",
          textTransform: "uppercase",
          flex: "none",
        }}
      >
        {title}
      </Typography>
      <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
        {caption}
      </Typography>
    </Stack>
    <Typography
      sx={{
        ...TABULAR_NUMERALS,
        fontWeight: 700,
        flex: "none",
        color: muted ? "text.disabled" : undefined,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

/** One part of a bar, as wide as its share of the axis. */
const Segment = ({
  share,
  color,
  label,
}: {
  share: number;
  color: string;
  label: string;
}) => {
  const { theme } = useTheme();
  return (
    <Box
      sx={{
        width: `${share}%`,
        flex: "none",
        bgcolor: color,
        color: theme.palette.getContrastText(color),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {share >= MIN_LABELLED_SHARE && (
        <Typography
          variant="caption"
          sx={{ ...TABULAR_NUMERALS, fontWeight: 600 }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

/**
 * What the agreement pays in a lost case and at each success step, as bars on
 * one axis that ends at the highest fee the steps can reach.
 */
const CfaOutcomeLadder = ({
  terms: { noWinFee, atRisk, steps },
  baseCost,
  noWinFeePercentage,
  triggerLabel,
  ranges,
}: {
  terms: CfaTerms;
  baseCost: number;
  noWinFeePercentage: number;
  /** Name of what the steps are triggered by. */
  triggerLabel: string;
  /** Per step, the trigger values it decides the fee for. */
  ranges: string[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "agreement.conditionalFeeAgreement",
  });

  const guaranteedColor = lighten(
    theme.palette.primary.main,
    GUARANTEED_LIGHTEN
  );
  const atRiskColor = lighten(theme.palette.primary.main, AT_RISK_LIGHTEN);

  // room for the most a success fee of 100% comes to — with all base costs at
  // risk — or for the highest total if any exceeds that
  const axisMax = Math.max(2 * baseCost, ...steps.map((step) => step.total));
  const share = (amount: number) => (axisMax > 0 ? (amount / axisMax) * 100 : 0);

  const track = {
    position: "relative",
    height: theme.spacing(4.25),
    bgcolor: theme.palette.action.hover,
    borderRadius: 1.5,
    overflow: "hidden",
  } as const;

  return (
    <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <RowHeading
          title={t("configurator.ladder.lost")}
          caption={
            noWinFee > 0
              ? t("configurator.ladder.lostWithFee", {
                  percentage: percentage.format(noWinFeePercentage),
                })
              : t("configurator.ladder.lostWithoutFee")
          }
          value={currency.format(noWinFee)}
          muted={noWinFee === 0}
        />
        <Box sx={track}>
          {/* outline of the base costs the fee falls short of */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: `${share(baseCost)}%`,
              border: `1.5px dashed ${theme.palette.divider}`,
              borderRadius: 1.5,
            }}
          />
          {noWinFee > 0 ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                display: "flex",
                width: "100%",
              }}
            >
              <Segment
                share={share(noWinFee)}
                color={guaranteedColor}
                label={currency.format(noWinFee)}
              />
            </Box>
          ) : (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                pl: 1.5,
                fontWeight: 600,
                color: "text.disabled",
              }}
            >
              {t("configurator.ladder.nothingDue", {
                value: currency.format(baseCost),
              })}
            </Typography>
          )}
        </Box>
      </Box>

      {steps.map((step, index) => (
        <Box key={index} sx={{ mb: 1.75 }}>
          <RowHeading
            color={stepColor(theme, index)}
            title={t("configurator.ladder.won", {
              step: t("successCondition.step", { count: index + 1 }),
            })}
            caption={t("configurator.ladder.wonCaption", {
              trigger: triggerLabel,
              range: ranges[index],
              percentage: percentage.format(step.appliedPercentage),
            })}
            value={currency.format(step.total)}
          />
          <Box sx={track}>
            <Box sx={{ display: "flex", height: "100%" }}>
              {noWinFee > 0 && (
                <Segment
                  share={share(noWinFee)}
                  color={guaranteedColor}
                  label={currency.format(noWinFee)}
                />
              )}
              {atRisk > 0 && (
                <Segment
                  share={share(atRisk)}
                  color={atRiskColor}
                  label={currency.format(atRisk)}
                />
              )}
              {step.uplift > 0 && (
                <Segment
                  share={share(step.uplift)}
                  color={stepColor(theme, index)}
                  label={`+${currency.format(step.uplift)}`}
                />
              )}
            </Box>
            {/* where the base costs end and the success fee starts */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${share(baseCost)}%`,
                borderLeft: `2px solid ${theme.palette.primary.main}`,
                opacity: 0.5,
              }}
            />
          </Box>
        </Box>
      ))}

      <Box sx={{ position: "relative", height: theme.spacing(2.5) }}>
        <Typography
          variant="caption"
          sx={{ position: "absolute", left: 0, color: "text.disabled" }}
        >
          {currency.format(0)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: `${share(baseCost)}%`,
            transform: "translateX(-50%)",
            fontWeight: 600,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {t("configurator.ladder.baseCosts", {
            value: currency.format(baseCost),
          })}
        </Typography>
        <Typography
          variant="caption"
          sx={{ position: "absolute", right: 0, color: "text.disabled" }}
        >
          {currency.format(axisMax)}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={2.25}
        useFlexGap
        sx={{ flexWrap: "wrap", pt: 1.25 }}
      >
        {[
          [guaranteedColor, t("configurator.ladder.legend.noWinFee")],
          [atRiskColor, t("configurator.ladder.legend.atRisk")],
          [
            theme.palette.data.positive.main,
            t("configurator.ladder.legend.uplift"),
          ],
        ].map(([color, label]) => (
          <Stack
            key={label}
            direction="row"
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <Swatch color={color} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default CfaOutcomeLadder;
